import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../context/AuthContext'

const DEFAULT_PROGRAMS = [
  {
    name: 'Seated Exercises',
    description: 'Chair-based exercises suitable for limited mobility patients',
    exercises: [
      { title: 'Seated Kicks', description: 'Seated knee extension', instructions: 'Sit upright in chair. Straighten one leg, hold 2 seconds, lower slowly. Alternate legs.', sets: 3, reps: 10 },
      { title: 'Seated Marches', description: 'Seated hip flexion', instructions: 'Sit upright. Lift one knee toward chest, lower, then lift the other. Keep back straight.', sets: 3, reps: 10 },
      { title: 'Ankle Pumps', description: 'Ankle range of motion and circulation', instructions: 'Sit with feet flat. Pump ankles up and down rhythmically. Both feet together.', sets: 3, reps: 20 },
      { title: 'Seated Hip Abductions', description: 'Hip abductor strengthening', instructions: 'Sit upright. Slide one foot out to the side as far as comfortable, return slowly. Alternate sides.', sets: 3, reps: 10 }
    ]
  }
]

export default function ApplyProgramModal({ patientId, onClose, onApplied }) {
  const { profile } = useAuth()
  const [templates, setTemplates] = useState([])
  const [availableExercises, setAvailableExercises] = useState([])
  const [selected, setSelected] = useState(null)
  const [editableExercises, setEditableExercises] = useState([])
  const [applying, setApplying] = useState(false)
  const [step, setStep] = useState('select')
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    fetchTemplates()
    supabase.from('exercises').select('*').eq('clinic_id', profile.clinic_id).order('title')
      .then(({ data }) => setAvailableExercises(data || []))
  }, [])

  async function fetchTemplates() {
    const { data } = await supabase
      .from('diagnosis_templates')
      .select('*, diagnosis_template_exercises(*, exercises(*))')
      .eq('clinic_id', profile.clinic_id)
      .order('created_at')
    setTemplates(data || [])
    setLoading(false)
  }

  async function selectTemplate(template) {
    setSelected(template)
    setEditableExercises(template.diagnosis_template_exercises.map(te => ({
      exercise_id: te.exercise_id,
      title: te.exercises?.title,
      sets: te.sets || '',
      reps: te.reps || '',
      duration_seconds: te.duration_seconds || '',
      frequency: te.frequency || 'daily',
      num_weeks: te.num_weeks || ''
    })))
    setStep('review')
  }

  function addExerciseToReview(exerciseId) {
    const ex = availableExercises.find(x => x.id === exerciseId)
    if (!ex) return
    setEditableExercises(prev => [...prev, {
      exercise_id: ex.id,
      title: ex.title,
      sets: ex.sets || '',
      reps: ex.reps || '',
      duration_seconds: ex.duration_seconds || '',
      frequency: 'daily',
      num_weeks: ''
    }])
  }

  function updateExercise(index, field, value) {
    setEditableExercises(prev => prev.map((e, i) => i === index ? { ...e, [field]: value } : e))
  }

  function removeExercise(index) {
    setEditableExercises(prev => prev.filter((_, i) => i !== index))
  }

  async function handleApply() {
    setApplying(true)
    const { data: existingAssignments } = await supabase
      .from('assignments')
      .select('sort_order')
      .eq('patient_id', patientId)
      .order('sort_order', { ascending: false })
      .limit(1)

    const startOrder = existingAssignments?.[0]?.sort_order + 1 || 0

    await supabase.from('assignments').insert(
      editableExercises.map((e, i) => ({
        patient_id: patientId,
        exercise_id: e.exercise_id,
        sets: e.sets ? parseInt(e.sets) : null,
        reps: e.reps ? parseInt(e.reps) : null,
        duration_seconds: e.duration_seconds ? parseInt(e.duration_seconds) : null,
        frequency: e.frequency,
        num_weeks: e.num_weeks ? parseInt(e.num_weeks) : null,
        sort_order: startOrder + i
      }))
    )

    setApplying(false)
    onApplied()
    onClose()
  }

  async function seedDefaultPrograms() {
    setLoading(true)
    for (const program of DEFAULT_PROGRAMS) {
      const { data: template } = await supabase
        .from('diagnosis_templates')
        .insert({ clinic_id: profile.clinic_id, name: program.name, description: program.description, is_default: true })
        .select()
        .single()

      if (!template) continue

      for (const ex of program.exercises) {
        let exerciseId
        const { data: existing } = await supabase
          .from('exercises')
          .select('id')
          .eq('clinic_id', profile.clinic_id)
          .ilike('title', ex.title)
          .single()

        if (existing) {
          exerciseId = existing.id
        } else {
          const { data: newEx } = await supabase
            .from('exercises')
            .insert({
              clinic_id: profile.clinic_id,
              title: ex.title,
              description: ex.description,
              instructions: ex.instructions,
              sets: ex.sets || null,
              reps: ex.reps || null,
              duration_seconds: ex.duration_seconds || null
            })
            .select()
            .single()
          exerciseId = newEx?.id
        }

        if (exerciseId) {
          await supabase.from('diagnosis_template_exercises').insert({
            template_id: template.id,
            exercise_id: exerciseId,
            sets: ex.sets || null,
            reps: ex.reps || null,
            duration_seconds: ex.duration_seconds || null,
            frequency: 'daily'
          })
        }
      }
    }
    await fetchTemplates()
  }

  const filtered = templates.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.description?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: 'var(--surface)', borderRadius: '20px 20px 0 0',
        width: '100%', maxWidth: 640, maxHeight: '85vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column'
      }}>

        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 17, margin: 0 }}>
            {step === 'select' ? 'Apply program' : selected?.name}
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {step === 'review' && (
              <button type="button" className="secondary" onClick={() => { setStep('select'); setSearch('') }} style={{ fontSize: 13, padding: '6px 12px' }}>← Back</button>
            )}
            <button type="button" className="secondary" onClick={onClose} style={{ fontSize: 13, padding: '6px 12px' }}>Cancel</button>
          </div>
        </div>

        {step === 'select' && templates.length > 0 && (
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--border)' }}>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', fontSize: 16, pointerEvents: 'none' }}>🔍</span>
              <input placeholder="Search programs..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 40 }} autoFocus />
              {search && (
                <span onClick={() => setSearch('')} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</span>
              )}
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>

          {step === 'select' && (
            <>
              {loading && <p>Loading...</p>}

              {!loading && templates.length === 0 && (
                <div className="empty-state" style={{ padding: '2rem 1rem' }}>
                  <div className="empty-state-icon">📋</div>
                  <h2 style={{ marginBottom: 8, fontSize: 16 }}>No programs yet</h2>
                  <p style={{ marginBottom: '1.5rem' }}>Load the sample program to get started quickly</p>
                  <button onClick={seedDefaultPrograms}>Load sample program</button>
                </div>
              )}

              {!loading && templates.length > 0 && filtered.length === 0 && (
                <div className="empty-state" style={{ padding: '2rem 1rem' }}>
                  <div className="empty-state-icon">🔍</div>
                  <h2 style={{ marginBottom: 8, fontSize: 16 }}>No results</h2>
                  <p>No programs match "{search}"</p>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filtered.map(template => (
                  <div key={template.id} className="card card-hover" onClick={() => selectTemplate(template)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 15, margin: '0 0 4px' }}>{template.name}</p>
                        {template.description && <p style={{ fontSize: 13, margin: '0 0 8px' }}>{template.description}</p>}
                        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: 0 }}>
                          {template.diagnosis_template_exercises?.length || 0} exercises
                        </p>
                      </div>
                      <span style={{ color: 'var(--brand)', fontSize: 20, marginLeft: 12 }}>→</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {step === 'review' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 4px' }}>
                Review and adjust before applying to the patient's program
              </p>

              <select
                defaultValue=""
                onChange={e => {
                  if (!e.target.value) return
                  addExerciseToReview(e.target.value)
                  e.target.value = ''
                }}
              >
                <option value="">+ Add another exercise from library...</option>
                {availableExercises
                  .filter(ex => !editableExercises.find(e => e.exercise_id === ex.id))
                  .map(ex => <option key={ex.id} value={ex.id}>{ex.title}</option>)
                }
              </select>

              {editableExercises.length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--text-tertiary)', textAlign: 'center', padding: '1rem 0' }}>
                  No exercises — add one from your library above
                </p>
              )}

              {editableExercises.map((ex, i) => (
                <div key={i} style={{ padding: '12px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', margin: 0 }}>{ex.title}</p>
                    <button type="button" className="danger" onClick={() => removeExercise(i)} style={{ fontSize: 12 }}>Remove</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
                    <div className="form-field">
                      <label className="form-label" style={{ fontWeight: 400, fontSize: 12 }}>Sets</label>
                      <input type="number" placeholder="3" min="1" value={ex.sets} onChange={e => updateExercise(i, 'sets', e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label className="form-label" style={{ fontWeight: 400, fontSize: 12 }}>Reps</label>
                      <input type="number" placeholder="10" min="1" value={ex.reps} onChange={e => updateExercise(i, 'reps', e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label className="form-label" style={{ fontWeight: 400, fontSize: 12 }}>Duration (sec)</label>
                      <input type="number" placeholder="30" min="1" value={ex.duration_seconds} onChange={e => updateExercise(i, 'duration_seconds', e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label className="form-label" style={{ fontWeight: 400, fontSize: 12 }}>Weeks</label>
                      <input type="number" placeholder="4" min="1" value={ex.num_weeks} onChange={e => updateExercise(i, 'num_weeks', e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={handleApply}
                disabled={applying || editableExercises.length === 0}
                style={{ marginTop: 8, padding: 14, fontSize: 15 }}
              >
                {applying ? 'Applying...' : 'Apply ' + editableExercises.length + ' exercise' + (editableExercises.length !== 1 ? 's' : '') + ' to program'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
